import { db } from './db';
import { newId } from './ids';
import { syncNow } from './sync';
import { useSyncStore } from '@/stores/syncStore';
import { maxPerShift, unitOf, type MeterUnit } from './meter';
import { diarySiteOf } from './fleet';
import type {
  ActivityType, DailyDiary, DiaryActivity, DiaryStatus, DiaryTimeAllocation,
  Machine, Setting, Shift, TimeCategory,
} from '@/types/models';

/**
 * The diary is the only place machine hours are entered, so it is the only
 * place they can be entered wrongly. Everything here mirrors a rule the
 * database also enforces — the client checks first so a supervisor at the
 * face gets told immediately, offline, instead of discovering it on sync.
 */

export interface DiaryDraft {
  id: string;
  diary_date: string;
  machine_id: string;
  shift_id: string;
  operator_id: string | null;
  opening_hour_meter: number | null;
  closing_hour_meter: number | null;
  /** hours = hour meter, km = odometer. From the diary once saved, else the machine. */
  meter_unit?: MeterUnit;
  scheduled_hours: number;
  status: DiaryStatus;
  comments: string | null;
  client_rev: number;
  /** Who captured it — set once saved. Decides who may change it (0046). */
  created_by?: string | null;
  allocations: AllocationDraft[];
  activities: ActivityDraft[];
}

export interface AllocationDraft {
  id: string;
  time_category_id: string;
  hours: number;
  note: string | null;
  breakdown_id?: string | null;
  weather_event_id?: string | null;
}

export interface ActivityDraft {
  id: string;
  activity_type_id: string;
  hours: number | null;
  loads: number | null;
  payload_assumption_t: number | null;
  tonnes_measured: number | null;
  /** Concrete activities (0052): m³ delivered or pumped, and the branch. */
  volume_m3: number | null;
  branch_site_id: string | null;
  blast_id: string | null;
  source_location_id: string | null;
  destination_location_id: string | null;
  /** 0068: the weighbridge ticket's material and product. */
  material_id?: string | null;
  product_id?: string | null;
  comments: string | null;
}

/* ------------------------------------------------------------------ */
/* Computations - the same arithmetic the server does                  */
/* ------------------------------------------------------------------ */

export function engineHours(d: Pick<DiaryDraft, 'opening_hour_meter' | 'closing_hour_meter'>): number | null {
  if (d.opening_hour_meter == null || d.closing_hour_meter == null) return null;
  return round(d.closing_hour_meter - d.opening_hour_meter, 2);
}

export function allocatedHours(allocations: AllocationDraft[]): number {
  return round(allocations.reduce((sum, a) => sum + (Number(a.hours) || 0), 0), 2);
}

export function estimatedTonnes(a: ActivityDraft): number {
  return round((a.loads ?? 0) * (a.payload_assumption_t ?? 0), 2);
}

/** Payload stamped onto the activity at capture time, so changing the site
 *  assumption later never rewrites history. */
export function payloadFor(machine: Machine | undefined, settings: Setting[] | undefined): number {
  const fallback = Number(
    settings?.find((s) => s.key === 'adt_payload_assumption_t')?.value ?? 30,
  );
  return machine?.default_payload_t ?? fallback;
}

export function settingNumber(settings: Setting[] | undefined, key: string, fallback: number): number {
  const v = settings?.find((s) => s.key === key)?.value;
  return v == null ? fallback : Number(v);
}

export function settingBool(settings: Setting[] | undefined, key: string, fallback: boolean): boolean {
  const v = settings?.find((s) => s.key === key)?.value;
  return v == null ? fallback : Boolean(v);
}

function round(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/* ------------------------------------------------------------------ */
/* Classification of allocated time, for the reconciliation bar        */
/* ------------------------------------------------------------------ */

export interface HourBreakdown {
  productive: number;
  standby: number;
  unplanned: number;
  planned: number;
  weather: number;
  total: number;
}

export function breakdown(
  allocations: AllocationDraft[],
  categories: TimeCategory[] | undefined,
): HourBreakdown {
  const out: HourBreakdown = {
    productive: 0, standby: 0, unplanned: 0, planned: 0, weather: 0, total: 0,
  };
  for (const a of allocations) {
    const c = categories?.find((x) => x.id === a.time_category_id);
    const h = Number(a.hours) || 0;
    out.total += h;
    if (!c) continue;
    if (c.is_productive) out.productive += h;
    else if (c.is_standby) out.standby += h;
    else if (c.is_weather) out.weather += h;
    else if (c.is_planned) out.planned += h;
    else if (c.is_downtime) out.unplanned += h;
  }
  out.total = round(out.total, 2);
  return out;
}

/* ------------------------------------------------------------------ */
/* Productive hours follow the hour meter (SAF-84)                     */
/* ------------------------------------------------------------------ */

/** The category productive time is booked to: PROD, else the first active
 *  productive category. */
export function productiveCategory(categories: TimeCategory[] | undefined): TimeCategory | undefined {
  const active = (categories ?? []).filter((c) => c.is_productive && c.is_active && !c.deleted_at);
  return active.find((c) => c.code === 'PROD') ?? active[0];
}

/** The engine hours productive time must equal, or null when it cannot be
 *  set from the meter: an odometer, a missing reading, or a reading the
 *  validation already refuses. */
export function meterProductiveHours(
  d: Pick<DiaryDraft, 'opening_hour_meter' | 'closing_hour_meter'>, unit: MeterUnit,
): number | null {
  if (unit !== 'hours') return null;
  const eng = engineHours(d);
  if (eng == null || eng < 0 || eng > maxPerShift(unit)) return null;
  return round(eng, 2);
}

/**
 * On an hour meter, productive hours are the engine hours: closing − opening
 * (Jakes, 15 Oct 2026). Books them to the productive category and clears any
 * other productive booking. Returns the allocations unchanged when the meter
 * cannot say (see meterProductiveHours).
 */
export function syncProductiveToMeter(
  allocations: AllocationDraft[],
  categories: TimeCategory[] | undefined,
  d: Pick<DiaryDraft, 'opening_hour_meter' | 'closing_hour_meter'>,
  unit: MeterUnit,
): AllocationDraft[] {
  const target = meterProductiveHours(d, unit);
  const prod = productiveCategory(categories);
  if (target == null || !prod) return allocations;

  const isProductive = (a: AllocationDraft) =>
    categories?.find((c) => c.id === a.time_category_id)?.is_productive ?? false;
  const current = allocations.filter(isProductive);
  if (current.length === (target > 0 ? 1 : 0)
      && (target === 0 || (current[0].time_category_id === prod.id && current[0].hours === target))) {
    return allocations;   // already right; keep the same array
  }

  const rest = allocations.filter((a) => !isProductive(a));
  if (target === 0) return rest;
  const keep = current.find((a) => a.time_category_id === prod.id);
  return [...rest, { id: keep?.id ?? newId(), time_category_id: prod.id, hours: target, note: keep?.note ?? null }];
}

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

/** The words the server uses too (0049), so a refusal reads the same
 *  whether the tablet or the database caught it. */
export function overBookedMessage(total: number, scheduled: number): string {
  return `Booked ${total} h is more than the ${scheduled} h shift. Take the extra off, `
    + 'or if the machine really worked longer, use "Shift was … h" to lengthen this day\'s shift.';
}

/** The shift length that would hold both the meter and the booked time,
 *  to the half hour. Offered by the "Shift was … h" button. */
export function longerShift(engine: number | null, total: number): number {
  return Math.min(24, Math.ceil(Math.max(engine ?? 0, total) * 2) / 2);
}

/** 0068: the same rule as v_activity_tonnes. An activity set to count only
 *  on a hauling machine adds no tonnes (and needs no blast) when a loader
 *  records it — the ADT's own entry carries those loads. */
export function countsAsProduction(type: ActivityType, machineHauling: boolean | undefined): boolean {
  return !type.production_on_hauling_only || !!machineHauling;
}

export interface DiaryIssue {
  field: string;
  message: string;
  blocking: boolean;
}

export async function validateDiary(
  d: DiaryDraft,
  ctx: { settings?: Setting[]; categories?: TimeCategory[]; activityTypes?: ActivityType[];
         meterUnit?: MeterUnit;
         /** The diary machine's type: activities for another type are refused (0052). */
         machineTypeId?: string;
         /** 0068: the machine hauls (ADT / dump truck). */
         machineHauling?: boolean;
         /** 0068: a branch diary — quarry blasts are not offered there. */
         atBranch?: boolean },
): Promise<DiaryIssue[]> {
  const unit: MeterUnit = ctx.meterUnit ?? d.meter_unit ?? 'hours';
  const issues: DiaryIssue[] = [];
  const tolerance = settingNumber(ctx.settings, 'diary_hours_tolerance', 0.5);
  const blockOver = settingBool(ctx.settings, 'diary_block_over_allocation', true);

  if (!d.machine_id) issues.push({ field: 'machine_id', message: 'Choose a machine', blocking: true });
  if (!d.shift_id) issues.push({ field: 'shift_id', message: 'Choose a shift', blocking: true });
  if (!d.diary_date) issues.push({ field: 'diary_date', message: 'Choose a date', blocking: true });

  if (d.opening_hour_meter == null) {
    issues.push({ field: 'opening_hour_meter', message: 'Opening reading is required', blocking: true });
  }
  if (d.closing_hour_meter == null) {
    issues.push({ field: 'closing_hour_meter', message: 'Closing reading is required', blocking: true });
  }

  const eng = engineHours(d);
  if (eng != null) {
    if (eng < 0) {
      issues.push({
        field: 'closing_hour_meter',
        message: 'Closing reading is below the opening reading',
        blocking: true,
      });
    } else if (eng > maxPerShift(unit)) {
      issues.push({
        field: 'closing_hour_meter',
        message: unit === 'km'
          ? `${eng} km in one shift — check the odometer readings`
          : `${eng} engine hours in one shift — check the readings`,
        blocking: true,
      });
    }
  }

  // Duplicate machine / date / shift - the database has a unique index on it.
  if (d.machine_id && d.diary_date && d.shift_id) {
    const clash = await db.daily_diary
      .where('[machine_id+diary_date]')
      .equals([d.machine_id, d.diary_date])
      .filter((x) => x.shift_id === d.shift_id && x.id !== d.id && !x.deleted_at)
      .first();
    if (clash) {
      issues.push({
        field: 'machine_id',
        message: 'A diary already exists for this machine, date and shift',
        blocking: true,
      });
    }
  }

  const total = allocatedHours(d.allocations);
  const variance = round(total - d.scheduled_hours, 2);
  // 0049: the tolerance is for time left unbooked, never for booking more
  // than the shift. A 9 h shift booked as 9 h productive + 0,5 h service let
  // utilisation read 105,9 % — productive hours larger than the hours the
  // machine was available. Same rule as app.check_diary_hours().
  if (variance > 0) {
    issues.push({
      field: 'allocations',
      // Said with the remedy. Without it the natural response was to widen
      // diary_hours_tolerance for the whole site until the error went away —
      // which is what happened on 17 September (0,5 h to 7 h).
      message: overBookedMessage(total, d.scheduled_hours),
      blocking: blockOver,
    });
  } else if (variance < -tolerance) {
    issues.push({
      field: 'allocations',
      message: `${round(Math.abs(variance), 2)} h of the shift is unaccounted for`,
      blocking: false,
    });
  }

  // SAF-84: on an hour meter, productive hours are the engine hours. The
  // editor books them from the readings; this catches anything else.
  // Kilometres and booked hours are different things, so an odometer is not
  // compared.
  const meterProd = meterProductiveHours(d, unit);
  if (meterProd != null) {
    const productive = round(breakdown(d.allocations, ctx.categories).productive, 2);
    if (productive !== meterProd) {
      issues.push({
        field: 'allocations',
        message: `Productive hours (${productive}) must equal the engine hours (${meterProd})`,
        blocking: true,
      });
    }
  }

  for (const a of d.activities) {
    const type = ctx.activityTypes?.find((t) => t.id === a.activity_type_id);
    if (!type) continue;
    if (type.requires_blast && !a.blast_id && countsAsProduction(type, ctx.machineHauling) && !ctx.atBranch) {
      issues.push({ field: `activity:${a.id}`, message: `${type.name} needs a blast reference`, blocking: true });
    }
    if (type.requires_destination && !a.destination_location_id && countsAsProduction(type, ctx.machineHauling)) {
      issues.push({ field: `activity:${a.id}`, message: `${type.name} needs a destination`, blocking: true });
    }
    if (a.loads != null && a.loads < 0) {
      issues.push({ field: `activity:${a.id}`, message: 'Loads cannot be negative', blocking: true });
    }
    // 0052: the same rules as app.diary_activity_rules().
    if (type.machine_type_id && ctx.machineTypeId && type.machine_type_id !== ctx.machineTypeId) {
      issues.push({ field: `activity:${a.id}`, message: `${type.name} is not recorded on this machine — remove it or choose another activity`, blocking: true });
    }
    if (type.concrete_measure) {
      if (!a.branch_site_id) {
        issues.push({ field: `activity:${a.id}`, message: `${type.name}: choose the branch`, blocking: true });
      }
      if (!(Number(a.volume_m3) > 0)) {
        issues.push({ field: `activity:${a.id}`, message: `${type.name}: enter the m³ ${type.concrete_measure}`, blocking: true });
      } else if (Number(a.volume_m3) > CONCRETE_MAX_M3) {
        issues.push({ field: `activity:${a.id}`, message: `${type.name}: ${a.volume_m3} m³ in one shift is more than one truck can do — check the figure`, blocking: true });
      }
    }
  }

  return issues;
}

/* ------------------------------------------------------------------ */
/* Persistence - local first, then the outbox                          */
/* ------------------------------------------------------------------ */

/**
 * Writes the whole aggregate to the local mirror and queues one outbox
 * entry. The push goes through upsert_diary(), which applies header,
 * allocations and activities in a single transaction, so a partially
 * synced diary cannot exist.
 */
export async function saveDiary(d: DiaryDraft, userId: string | null): Promise<void> {
  const now = new Date().toISOString();
  // 0053: a diary belongs to its vehicle's site (the quarry or a branch).
  const machine = d.machine_id ? await db.machines.get(d.machine_id) : undefined;

  const header: DailyDiary = {
    id: d.id,
    site_id: diarySiteOf(machine),
    diary_date: d.diary_date,
    machine_id: d.machine_id,
    shift_id: d.shift_id,
    operator_id: d.operator_id,
    opening_hour_meter: d.opening_hour_meter ?? 0,
    closing_hour_meter: d.closing_hour_meter ?? 0,
    engine_hours: engineHours(d) ?? 0,
    meter_unit: d.meter_unit ?? 'hours',
    scheduled_hours: d.scheduled_hours,
    status: d.status,
    comments: d.comments,
    client_rev: d.client_rev,
    synced_at: null,
    created_at: now,
    updated_at: now,
    // The author stays the author when someone else corrects the diary —
    // the server keeps it that way too (touch_row, 0028).
    created_by: d.created_by ?? userId,
    updated_by: userId,
    deleted_at: null,
  };

  const allocations: DiaryTimeAllocation[] = d.allocations
    .filter((a) => Number(a.hours) > 0)
    .map((a) => ({
      id: a.id,
      diary_id: d.id,
      time_category_id: a.time_category_id,
      hours: Number(a.hours),
      breakdown_id: a.breakdown_id ?? null,
      weather_event_id: a.weather_event_id ?? null,
      note: a.note,
      created_at: now, updated_at: now, created_by: userId, updated_by: userId, deleted_at: null,
    }));

  const activities: DiaryActivity[] = d.activities.map((a) => ({
    id: a.id,
    diary_id: d.id,
    activity_type_id: a.activity_type_id,
    hours: a.hours,
    loads: a.loads,
    payload_assumption_t: a.payload_assumption_t,
    tonnes_estimated: estimatedTonnes(a),
    tonnes_measured: a.tonnes_measured,
    volume_m3: a.volume_m3,
    branch_site_id: a.branch_site_id,
    blast_id: a.blast_id,
    source_location_id: a.source_location_id,
    destination_location_id: a.destination_location_id,
    material_id: a.material_id ?? null,
    product_id: a.product_id ?? null,
    comments: a.comments,
    created_at: now, updated_at: now, created_by: userId, updated_by: userId, deleted_at: null,
  }));

  const queued = {
    entity: 'diary' as const,
    entity_id: d.id,
    op: 'upsert' as const,
    payload: {
      diary: {
        id: header.id,
        site_id: header.site_id,
        diary_date: header.diary_date,
        machine_id: header.machine_id,
        shift_id: header.shift_id,
        operator_id: header.operator_id,
        opening_hour_meter: header.opening_hour_meter,
        closing_hour_meter: header.closing_hour_meter,
        scheduled_hours: header.scheduled_hours,
        status: header.status,
        comments: header.comments,
        client_rev: header.client_rev,
      },
      allocations: allocations.map((a) => ({
        id: a.id, time_category_id: a.time_category_id, hours: a.hours, note: a.note,
        breakdown_id: a.breakdown_id ?? null, weather_event_id: a.weather_event_id ?? null,
      })),
      activities: activities.map((a) => ({
        id: a.id,
        activity_type_id: a.activity_type_id,
        hours: a.hours,
        loads: a.loads,
        payload_assumption_t: a.payload_assumption_t,
        tonnes_measured: a.tonnes_measured,
        volume_m3: a.volume_m3 ?? null,
        branch_site_id: a.branch_site_id ?? null,
        blast_id: a.blast_id,
        source_location_id: a.source_location_id,
        destination_location_id: a.destination_location_id,
    material_id: a.material_id ?? null,
    product_id: a.product_id ?? null,
        comments: a.comments,
      })),
    },
  };

  // The aggregate and its send request commit together: a full disk or a
  // failed outbox write must not leave a diary that looks saved but never syncs.
  await db.transaction('rw', [db.daily_diary, db.diary_time_allocations, db.diary_activities, db.outbox], async () => {
    await db.daily_diary.put(header);
    await db.diary_time_allocations.where('diary_id').equals(d.id).delete();
    if (allocations.length) await db.diary_time_allocations.bulkPut(allocations);
    await db.diary_activities.where('diary_id').equals(d.id).delete();
    if (activities.length) await db.diary_activities.bulkPut(activities);
    await db.outbox.add({ ...queued, created_at: now, attempts: 0, next_attempt_at: now, last_error: null });
  });
  useSyncStore.getState().setPending(await db.outbox.count());
  void syncNow();
}

/** Load an existing diary out of the local mirror into an editable draft. */
export async function loadDraft(id: string): Promise<DiaryDraft | null> {
  const { header, allocations, activities } = await db.transaction('r',
    [db.daily_diary, db.diary_time_allocations, db.diary_activities], async () => ({
      header: await db.daily_diary.get(id),
      allocations: await db.diary_time_allocations.where('diary_id').equals(id).toArray(),
      activities: await db.diary_activities.where('diary_id').equals(id).toArray(),
    }));
  if (!header) return null;

  return {
    id: header.id,
    diary_date: header.diary_date,
    machine_id: header.machine_id,
    shift_id: header.shift_id,
    operator_id: header.operator_id,
    opening_hour_meter: header.opening_hour_meter,
    closing_hour_meter: header.closing_hour_meter,
    meter_unit: header.meter_unit ?? 'hours',
    scheduled_hours: header.scheduled_hours,
    status: header.status,
    comments: header.comments,
    client_rev: (header.client_rev ?? 1) + 1,
    created_by: header.created_by,
    allocations: allocations.map((a) => ({
      id: a.id, time_category_id: a.time_category_id, hours: a.hours, note: a.note,
        breakdown_id: a.breakdown_id ?? null, weather_event_id: a.weather_event_id ?? null,
    })),
    activities: activities.map((a) => ({
      id: a.id,
      activity_type_id: a.activity_type_id,
      hours: a.hours,
      loads: a.loads,
      payload_assumption_t: a.payload_assumption_t,
      tonnes_measured: a.tonnes_measured,
      volume_m3: a.volume_m3 ?? null,
      branch_site_id: a.branch_site_id ?? null,
      blast_id: a.blast_id,
      source_location_id: a.source_location_id,
      destination_location_id: a.destination_location_id,
    material_id: a.material_id ?? null,
    product_id: a.product_id ?? null,
      comments: a.comments,
    })),
  };
}

/** Most m³ one concrete activity may record in a shift. Same limit as
 *  app.diary_activity_rules() (0052). */
export const CONCRETE_MAX_M3 = 500;

/** The activities a machine may record: those for any machine, plus those
 *  for its own type (0052: concrete delivered on a mixer, pumped on a pump
 *  truck). The machine's own activities come first. */
export function activityTypesFor(types: ActivityType[], machine: Machine | undefined): ActivityType[] {
  const own = types.filter((t) => t.machine_type_id && t.machine_type_id === machine?.machine_type_id);
  const any = types.filter((t) => !t.machine_type_id);
  return [...own, ...any];
}

/** Hours booked to Service on every new diary: the daily pre-start
 *  service (SAF-84). The person capturing can change it or set it to 0. */
export const DEFAULT_SERVICE_HOURS = 0.5;

/** A new diary, pre-filled from the machine and shift where we can. */
export function newDraft(opts: {
  date: string;
  machine?: Machine;
  shift?: Shift;
  lastClosingMeter?: number | null;
  /** The Service time category; when given, the new diary books
   *  DEFAULT_SERVICE_HOURS to it. */
  serviceCategoryId?: string | null;
}): DiaryDraft {
  return {
    id: newId(),
    diary_date: opts.date,
    machine_id: opts.machine?.id ?? '',
    shift_id: opts.shift?.id ?? '',
    operator_id: null,
    // The opening reading is last shift's closing reading - the single
    // most common source of a wrong engine-hours figure if left to memory.
    opening_hour_meter: opts.lastClosingMeter ?? opts.machine?.current_hour_meter ?? null,
    closing_hour_meter: null,
    meter_unit: unitOf(opts.machine),
    scheduled_hours: opts.shift?.scheduled_hours ?? opts.machine?.expected_hours_per_day ?? 10,
    status: 'draft',
    comments: null,
    client_rev: 1,
    allocations: opts.serviceCategoryId
      ? [{ id: newId(), time_category_id: opts.serviceCategoryId, hours: DEFAULT_SERVICE_HOURS, note: null }]
      : [],
    activities: [],
  };
}

/** Last closing reading for a machine, so a new diary opens where the
 *  previous one closed. */
export async function lastClosingMeter(
  machineId: string, before: string, unit: MeterUnit = 'hours',
): Promise<number | null> {
  const rows = await db.daily_diary.where('machine_id').equals(machineId).toArray();
  // 0061: after a meter is replaced, the old meter's diaries are no starting
  // point (the machine's current reading is).
  const m = (await db.table('machines').get(machineId)) as { meter_replaced_at?: string | null } | undefined;
  const fitted = m?.meter_replaced_at
    ? new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Windhoek' }).format(new Date(m.meter_replaced_at)) : null;
  // Only readings on the same kind of meter: after a new odometer is
  // fitted, the last hour-meter reading is no starting point.
  const prior = rows
    .filter((r) => !r.deleted_at && r.diary_date < before && (r.meter_unit ?? 'hours') === unit)
    .filter((r) => !fitted || before <= fitted || r.diary_date >= fitted)
    .sort((a, b) => b.diary_date.localeCompare(a.diary_date));
  return prior[0]?.closing_hour_meter ?? null;
}
